import { PLATFORMS_SUPPORTED } from "../../../utils/constants";

export const hasAdvertiserPrimaryKpiConfigured = (summary) => {
  if (!summary || typeof summary !== "object") {
    return false;
  }

  return (
    typeof summary.primaryKpi === "string" &&
    summary.primaryKpi.trim().length > 0
  );
};

/**
 * Form draft shape from the admin modal: { primaryKpi, platformTargets }.
 */
const isManualFormPayloadMeaningful = (draft) => {
  if (!draft || typeof draft !== "object") {
    return false;
  }

  if (typeof draft.primaryKpi === "string" && draft.primaryKpi.trim()) {
    return true;
  }

  const { platformTargets } = draft;
  if (!platformTargets || typeof platformTargets !== "object") {
    return false;
  }

  return (
    PLATFORMS_SUPPORTED.some((platform) => {
      const row = platformTargets[platform];
      const n = Number(row?.targetCpa);
      return Number.isFinite(n) && n > 0;
    }) ||
    (Number.isFinite(Number(draft.volume)) && Number(draft.volume) > 0)
  );
};

/**
 * Returns true when advertiser.kpiSummary in RTDB is an admin manual override
 * (same shape as campaign-derived summary, but persisted on the advertiser).
 */
export const isStoredManualKpiSummaryMeaningful = (summary) => {
  if (!summary || typeof summary !== "object") {
    return false;
  }

  if (typeof summary.primaryKpi === "string" && summary.primaryKpi.trim()) {
    return true;
  }

  const parts = summary.targetCpaParts;
  const hasValidPart =
    Array.isArray(parts) &&
    parts.some(
      (p) =>
        typeof p?.targetCpa === "number" &&
        Number.isFinite(p.targetCpa) &&
        p.targetCpa > 0
    );

  if (hasValidPart) {
    return true;
  }

  const volume = Number(summary.volume);
  if (Number.isFinite(volume) && volume > 0) {
    return true;
  }

  return (
    typeof summary.targetCpa === "number" &&
    Number.isFinite(summary.targetCpa) &&
    summary.targetCpa > 0
  );
};

/**
 * Maps modal form draft to the kpiSummary shape stored on advertisers/{id}/kpiSummary.
 */
export const buildKpiSummaryFromManual = (draft) => {
  if (!isManualFormPayloadMeaningful(draft)) {
    return null;
  }

  const targetCpaParts = [];
  PLATFORMS_SUPPORTED.forEach((platform) => {
    const row = draft.platformTargets?.[platform];
    const n = Number(row?.targetCpa);
    if (!Number.isFinite(n) || n <= 0) {
      return;
    }
    const c = row?.currency;
    const currency =
      typeof c === "string" && c.trim() ? c.trim().toUpperCase() : null;
    targetCpaParts.push({
      platform,
      targetCpa: n,
      currency,
    });
  });

  const primaryKpiTrimmed =
    typeof draft.primaryKpi === "string" ? draft.primaryKpi.trim() : "";
  const primaryKpi = primaryKpiTrimmed || null;

  const volumenN = Number(draft.volume);
  const volume =
    Number.isFinite(volumenN) && volumenN > 0 ? Math.round(volumenN) : null;

  return {
    primaryKpi,
    targetCpaParts,
    volume: volume ?? null,
  };
};

/**
 * Prefers persisted admin kpiSummary on the advertiser when set;
 * otherwise uses campaign-derived kpiSummary from full data fetch.
 */
export const getEffectiveKpiSummary = ({ manual, derived }) => {
  if (isStoredManualKpiSummaryMeaningful(manual)) {
    return manual;
  }
  return derived ?? null;
};
